<template>
  <!-- Filter Dialog -->
  <v-dialog v-model="showFilterDialog" max-width="500">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-filter-variant</v-icon>
        {{ $t('filterDialog.title') }}
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <div class="text-subtitle-2 mb-2">{{ $t('filterDialog.itemTypes') }}</div>
        <div class="d-flex flex-wrap gap-2">
          <v-btn v-for="type in ITEM_TYPES"
                 :key="type"
                 @click="toggleTypeFilter(type)"
                 :variant="localTypeFilters[type] ? 'tonal' : 'outlined'"
                 :color="localTypeFilters[type] ? 'primary' : 'grey'"
                 size="small"
                 class="status-filter-btn">
            {{ $t('filterDialog.' + type) }}
          </v-btn>
        </div>
        <v-divider class="my-4"></v-divider>
        <div class="text-subtitle-2 mb-2">{{ $t('filterDialog.statuses') }}</div>
        <div v-if="statusTemplates.length > 0" class="d-flex flex-wrap gap-2" :key="filterKey">
          <v-btn v-for="status in statusTemplates"
                 :key="status.title"
                 @click="toggleFilter(status.title)"
                 :variant="localFilters[status.title] ? 'tonal' : 'outlined'"
                 :color="localFilters[status.title] ? status.color : 'grey'"
                 size="small"
                 class="status-filter-btn">
            <div class="status-color-indicator mr-2" :style="{ backgroundColor: status.color }"></div>
            {{ status.title }}
          </v-btn>
        </div>
        <div v-else class="text-center text-medium-emphasis">
          {{ $t('filterDialog.noAvailableStatus') }}
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn color="primary" variant="text" @click="toggleAllFilters">
          {{ allFiltersSelected ? $t('filterDialog.deselectAll') : $t('filterDialog.selectAll') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="showFilterDialog = false">
          {{ $t('filterDialog.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <div class="fab-container">
    <v-tooltip :text="$t('filterDialog.globalFilters')">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          icon
          size="large"
          :color="buttonColor"
          variant="tonal"
          @click="showFilterDialog = true"
          :class="{ 'filter-active-dark': hasActiveFilter && theme === 'dark', 'filter-active-light': hasActiveFilter && theme === 'light' }"
        >
          <v-icon size="24">mdi-filter-variant</v-icon>
        </v-btn>
      </template>
    </v-tooltip>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

const showFilterDialog = ref(false);
const theme = computed(() => store.state.theme);

// Use ref for better reactivity with object replacement
const localFilters = ref({});

// Key to force re-render of buttons when bulk operations happen
const filterKey = ref(0);

// Sync local filters with store
watch(() => store.state.statusFilters, (newFilters) => {
  if (newFilters && Object.keys(newFilters).length > 0) {
    localFilters.value = { ...newFilters };
  }
}, { immediate: true, deep: true });

// Item type filters: issues vs processes ("Type" = 'IssueTemplate' marks issues)
const ITEM_TYPES = ['issues', 'processes'];
const localTypeFilters = ref({});

if (Object.keys(store.state.itemTypeFilters).length === 0) {
  store.commit('setItemTypeFilters', { issues: true, processes: true });
}

watch(() => store.state.itemTypeFilters, (newFilters) => {
  if (newFilters && Object.keys(newFilters).length > 0) {
    localTypeFilters.value = { ...newFilters };
  }
}, { immediate: true, deep: true });

// Status templates loaded from DuckDB, restricted to statuses actually in use,
// with flags for which item types (issues/processes) use each status
const allStatusTemplates = ref([]);

const loadStatusTemplates = async () => {
  const conn = store.getters.getDBConnection;
  if (!conn) return;
  try {
    const result = await conn.query(`
      SELECT st.Title AS "title", st.Color AS "color", st.Sort AS "sort", st.IsDraft AS "isDraft",
             MAX(CASE WHEN i."Type" = 'IssueTemplate' THEN 1 ELSE 0 END) AS "usedByIssues",
             MAX(CASE WHEN COALESCE(i."Type", '') != 'IssueTemplate' THEN 1 ELSE 0 END) AS "usedByProcesses"
      FROM status_templates st
      INNER JOIN items i ON st.Id = i.StatusId
      GROUP BY st.Title, st.Color, st.Sort, st.IsDraft
    `);
    const uniqueStatuses = new Map();
    result.toArray()
      .map(row => row.toJSON())
      .filter(status => status && status.title)
      .sort((a, b) => {
        const sortDiff = Number(a.sort || 0) - Number(b.sort || 0);
        if (sortDiff !== 0) return sortDiff;
        return String(b.color || '').localeCompare(String(a.color || ''));
      })
      .forEach(status => {
        const existing = uniqueStatuses.get(status.title);
        if (existing) {
          // Same title used by several templates — merge type usage
          existing.usedByIssues = existing.usedByIssues || Number(status.usedByIssues) > 0;
          existing.usedByProcesses = existing.usedByProcesses || Number(status.usedByProcesses) > 0;
        } else {
          uniqueStatuses.set(status.title, {
            title: status.title,
            color: status.color || '#999999',
            sort: Number(status.sort || 0),
            isDraft: Boolean(status.isDraft),
            usedByIssues: Number(status.usedByIssues) > 0,
            usedByProcesses: Number(status.usedByProcesses) > 0
          });
        }
      });
    allStatusTemplates.value = Array.from(uniqueStatuses.values());
  } catch (error) {
    console.error('Error loading status templates for filter:', error);
  }
};

// Statuses shown in the dialog: only those used by at least one selected item type
const statusTemplates = computed(() =>
  allStatusTemplates.value.filter(status =>
    (localTypeFilters.value.issues && status.usedByIssues) ||
    (localTypeFilters.value.processes && status.usedByProcesses)
  )
);

// Dynamic filters state - use store to persist across pages
const filters = computed({
  get: () => store.state.statusFilters,
  set: (value) => store.commit('setStatusFilters', value)
});

const typeFilters = computed(() => store.state.itemTypeFilters);

// Check if all filters (statuses and types) are selected
const allFiltersSelected = computed(() => {
  const filterValues = Object.values(localFilters.value);
  const typeValues = Object.values(localTypeFilters.value);
  return filterValues.length > 0 && filterValues.every(v => v === true) &&
         typeValues.every(v => v === true);
});

// Only visible statuses count: a deselected status hidden by the type
// selection has no effect until its type is selected again
const hasActiveStatusFilter = computed(() =>
  statusTemplates.value.some(status => localFilters.value[status.title] === false)
);

const hasActiveTypeFilter = computed(() => {
  const typeValues = Object.values(localTypeFilters.value);
  return typeValues.length > 0 && typeValues.some(v => v === false);
});

// Check if any filter is active (a status or an item type is deselected)
const hasActiveFilter = computed(() => hasActiveStatusFilter.value || hasActiveTypeFilter.value);

// Toggle individual filter
const toggleFilter = (statusTitle) => {
  const newFilters = { ...localFilters.value };
  newFilters[statusTitle] = !newFilters[statusTitle];
  localFilters.value = newFilters;
  store.commit('setStatusFilters', newFilters);
};

// Toggle individual item type filter
const toggleTypeFilter = (type) => {
  const newFilters = { ...localTypeFilters.value };
  newFilters[type] = !newFilters[type];
  localTypeFilters.value = newFilters;
  store.commit('setItemTypeFilters', newFilters);
};

// Toggle all filters (select all or deselect all), statuses and types together.
// Acts on ALL statuses, not just the visible ones, so the result is predictable
// regardless of the current type selection
const toggleAllFilters = async () => {
  const newValue = !allFiltersSelected.value;
  const newFilters = {};
  allStatusTemplates.value.forEach(status => {
    newFilters[status.title] = newValue;
  });
  const newTypeFilters = {};
  ITEM_TYPES.forEach(type => {
    newTypeFilters[type] = newValue;
  });
  localFilters.value = { ...newFilters };
  localTypeFilters.value = { ...newTypeFilters };
  filterKey.value++;
  store.commit('setStatusFilters', newFilters);
  store.commit('setItemTypeFilters', newTypeFilters);
  await nextTick();
  await applyFilters();
};

const applyStatusFilter = async () => {
  // Filter on the visible statuses only (those of the selected item types) —
  // hidden statuses are already excluded by the intersecting type filter
  const visibleTitles = statusTemplates.value.map(status => status.title);
  const selectedStatuses = visibleTitles.filter(title => filters.value[title]);
  const totalStatuses = visibleTitles.length;

  // If all statuses are selected, remove the status filter
  if (selectedStatuses.length === totalStatuses) {
    await store.state.filterItemsContext.removeFilter('statusFilter');
    await store.state.filterStatusHistoryContext.removeFilter('statusHistoryFilter');
    return;
  }

  // If no statuses selected, show nothing
  if (selectedStatuses.length === 0) {
    await store.state.filterItemsContext.updateFilter('statusFilter', []);
    await store.state.filterStatusHistoryContext.updateFilter('statusHistoryFilter', []);
    return;
  }

  // Build SQL query for partial selection
  const statusList = selectedStatuses.map(s => `'${s.replace(/'/g, "''")}'`).join(', ');

  const itemsQuery = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    INNER JOIN status_templates st ON i.StatusId = st.Id
    WHERE st.Title IN (${statusList})
  `;

  const statusHistoryQuery = `
    SELECT DISTINCT sht.subItemId, sht.validFrom, sht.statusId
    FROM status_history_temporal sht
    INNER JOIN status_templates st ON sht.statusId = st.Id
    WHERE st.Title IN (${statusList})
  `;

  await store.state.filterItemsContext.updateFilterWithQuery('statusFilter', itemsQuery);
  await store.state.filterStatusHistoryContext.updateFilterWithQuery('statusHistoryFilter', statusHistoryQuery);
};

const applyTypeFilter = async () => {
  const selected = ITEM_TYPES.filter(type => typeFilters.value[type]);

  // If all (or no configured) types are selected, remove the type filter
  if (selected.length === ITEM_TYPES.length || Object.keys(typeFilters.value).length === 0) {
    await store.state.filterItemsContext.removeFilter('itemTypeFilter');
    await store.state.filterStatusHistoryContext.removeFilter('itemTypeHistoryFilter');
    return;
  }

  // If no types selected, show nothing
  if (selected.length === 0) {
    await store.state.filterItemsContext.updateFilter('itemTypeFilter', []);
    await store.state.filterStatusHistoryContext.updateFilter('itemTypeHistoryFilter', []);
    return;
  }

  // Exactly one type selected
  const predicate = selected[0] === 'issues'
    ? `i."Type" = 'IssueTemplate'`
    : `COALESCE(i."Type", '') != 'IssueTemplate'`;

  const itemsQuery = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    WHERE ${predicate}
  `;

  // status_history_temporal has no type column, so go through items
  const statusHistoryQuery = `
    SELECT DISTINCT sht.subItemId, sht.validFrom, sht.statusId
    FROM status_history_temporal sht
    INNER JOIN items i ON sht.subItemId = i.SubItemId
    WHERE ${predicate}
  `;

  await store.state.filterItemsContext.updateFilterWithQuery('itemTypeFilter', itemsQuery);
  await store.state.filterStatusHistoryContext.updateFilterWithQuery('itemTypeHistoryFilter', statusHistoryQuery);
};

const applyFilters = async () => {
  if (!store.getters.isDuckDBInitialized || !store.getters.getDataLoaded) {
    return;
  }

  const conn = store.getters.getDBConnection;
  if (!conn) return;

  try {
    await applyStatusFilter();
    await applyTypeFilter();
  } catch (error) {
    console.error('Error applying status filters:', error);
  }
};

// Watch for loaded templates to initialize filters (all selected by default)
watch(allStatusTemplates, (newTemplates) => {
  if (newTemplates.length > 0) {
    if (Object.keys(filters.value).length === 0) {
      const newFilters = {};
      newTemplates.forEach(status => {
        newFilters[status.title] = true;
      });
      store.commit('setStatusFilters', newFilters);
      applyFilters();
    }
  }
}, { immediate: true });

// Watch for changes in filters and apply them in realtime
watch(filters, () => {
  applyFilters();
}, { deep: true });

watch(typeFilters, () => {
  applyFilters();
}, { deep: true });

// Watch for data loaded to load templates and reapply existing filters
watch(() => store.getters.getDataLoaded, (isLoaded) => {
  if (isLoaded) {
    loadStatusTemplates();
    if (Object.keys(filters.value).length > 0) {
      applyFilters();
    }
  }
}, { immediate: true });

// Reapply when a filter context is re-initialized elsewhere (e.g. App.vue after
// data load, or ActivityPage rebuilding status_history_temporal), which wipes
// registered filters. Guarded on the filter being absent so reapplying (which
// bumps filtersLastUpdated itself) cannot loop.
watch(() => store.state.filterItemsContext.filtersLastUpdated.value, () => {
  if (!store.getters.getDataLoaded) return;
  const registered = store.state.filterItemsContext.filters;
  if ((hasActiveStatusFilter.value && !('statusFilter' in registered)) ||
      (hasActiveTypeFilter.value && !('itemTypeFilter' in registered))) {
    applyFilters();
  }
});

watch(() => store.state.filterStatusHistoryContext.filtersLastUpdated.value, () => {
  if (!store.getters.getDataLoaded) return;
  const registered = store.state.filterStatusHistoryContext.filters;
  if ((hasActiveStatusFilter.value && !('statusHistoryFilter' in registered)) ||
      (hasActiveTypeFilter.value && !('itemTypeHistoryFilter' in registered))) {
    applyFilters();
  }
});

const buttonColor = computed(() => theme.value === 'dark' ? 'grey-lighten-5' : 'grey-darken-3');
</script>

<style scoped>
.fab-container {
  position: fixed;
  bottom: 16px;
  right: 16px;
  /* Above page content (charts create their own stacking contexts) but below
     Vuetify overlays like dialogs and menus (z-index 2400) */
  z-index: 1000;
}

.status-color-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-filter-btn {
  text-transform: none !important;
}

.gap-2 {
  gap: 8px;
}

.filter-active-light {
  outline: 4px solid #4f5b64 !important;
  outline-offset: 1px;
}

.filter-active-dark {
  outline: 4px solid #253238 !important;
  outline-offset: 1px;
}
</style>
